"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.manageAthenaRecurringDateConfigOutputs = exports.constructAthenaResultSchema = exports.constructSqlQueryString = exports.evaluateFormulaBindings = void 0;
const common_1 = require("@finley-technologies/common");
const finley_date_1 = require("@finley-technologies/finley-date");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const _isFormula = (input) => {
    return typeof input === 'string' && input.startsWith('=');
};
const evaluateVariables = (params) => {
    const { variables } = params;
    const results = [];
    const errors = [];
    const sanitizedFormulas = variables
        .filter(({ value }) => _isFormula(value))
        .map(({ value }) => value.slice(1));
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, sanitizedFormulas);
    const variableNames = new Set();
    for (const { name, value, parseNumber } of variables) {
        let evalutedValue;
        if (!_isFormula(value)) {
            // *** Manage non-formula value by just parsing it directly ***
            evalutedValue = common_1.stringConversion.parseStringValueToSqlPrimitive(
            // We're actually parsing a string here to potentially another type.
            // "parseNumber" here represents the trigger whether to attempt parsing
            // the input value (which is a string)
            { value, parseString: parseNumber });
        }
        else {
            // *** Manage formula value ***
            const formula = value.slice(1);
            const formulaErrors = (0, formula_engine_1.getFormulaErrors)(formula, variableNames);
            if (formulaErrors.errorMessages.length > 0) {
                formulaErrors.errorMessages.forEach((err) => errors.push(err));
                continue;
            }
            const evaluatedFormula = formulaEngine.evaluate(formula);
            if (!finley_types_1.typeguards.isSqlPrimitive(evaluatedFormula)) {
                errors.push(`Evaluated non-primitive formula [value:${value}] [result:${evaluatedFormula}]`);
                continue;
            }
            evalutedValue = evaluatedFormula;
        }
        formulaEngine.setVariables({ [name]: evalutedValue });
        variableNames.add(name);
        results.push({ name, value: evalutedValue });
    }
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    return { isValid: true, results };
};
const evaluateFormulaBindings = ({ variables, bindings, }) => {
    const variableNames = new Set(Object.keys(variables));
    const formulas = bindings.filter((binding) => _isFormula(binding));
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, formulas.map((formula) => formula.slice(1)));
    formulaEngine.setVariables(variables);
    const evaluatedBindings = [];
    bindings.forEach((binding) => {
        if (!_isFormula(binding)) {
            evaluatedBindings.push(binding);
            return;
        }
        const formula = binding.slice(1);
        const errors = (0, formula_engine_1.getFormulaErrors)(formula, variableNames);
        (0, common_1.finleyAssert)(errors.errorMessages.length === 0, new common_1.error.FinleyError(`Errors detected when evaluating binding formulas.\nMessages: ${errors.errorMessages}\nMissing Functions: ${errors.missingFunctions}\nMissing Variables: ${errors.missingVariables}\n`, {
            metadata: {
                logMeta: {
                    errorMessages: JSON.stringify(errors.errorMessages),
                    missingFunctions: JSON.stringify(errors.missingFunctions),
                    missingVariables: JSON.stringify(errors.missingVariables),
                },
            },
        }));
        const evalutedBinding = formulaEngine.evaluate(formula);
        (0, common_1.finleyAssert)(finley_types_1.typeguards.isSqlPrimitive(evalutedBinding), `Expected evaluted binding formula to resolve to string|number|null|boolean, but got ${evalutedBinding}`);
        evaluatedBindings.push(evalutedBinding);
    });
    return evaluatedBindings;
};
exports.evaluateFormulaBindings = evaluateFormulaBindings;
const constructSqlQueryString = ({ query, bindings, variables, }) => {
    const variableResults = evaluateVariables({ variables });
    if (!variableResults.isValid) {
        return { isValid: false, errors: variableResults.errors };
    }
    const { keyMap, errors } = (0, formula_engine_1.keyByVariableName)(variableResults.results);
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    const evaluatedBindings = (0, exports.evaluateFormulaBindings)({
        variables: keyMap,
        bindings,
    });
    const queryString = common_1.datalake.athena.generateBindedSqlString({
        query,
        bindings: evaluatedBindings,
    });
    return { isValid: true, queryString };
};
exports.constructSqlQueryString = constructSqlQueryString;
// TODO: add unit tests on interpretation here
const interpretValueType = (val) => {
    if (typeof val === 'boolean') {
        return 'boolean';
    }
    if (typeof val === 'number') {
        return 'number';
    }
    if (typeof val === 'string') {
        if (['true', 'false'].includes(val.toLowerCase())) {
            return 'boolean';
        }
        // TODO: add "date" support
        // if (
        //   commonDate.isStringAbleToBeDateType(val) ||
        //   commonTypeguards.isFullDateStr(val) ||
        //   commonTypeguards.isFullDateTimeStr(val)
        // ) {
        //   return 'date';
        // }
        if (common_1.utils.isNumericString(val)) {
            return 'number';
        }
        if (typeof common_1.stringConversion.currencyStrToNumber(val, finley_types_1.Bank.Currency.USD) === 'number') {
            return 'number';
        }
        return 'string';
    }
    throw new common_1.error.FinleyError(`Detected unsupported [type:${typeof val}] while interpreting [val:${val}] for Athena schema`);
};
const constructAthenaResultSchema = (items, options = {}) => {
    var _a;
    const schema = [];
    const keyTypeMap = new Map();
    const errors = [];
    const targetItems = options.onlyEvaluateFirst ? items.slice(0, 1) : items;
    for (const item of targetItems) {
        for (const [key, val] of Object.entries(item)) {
            try {
                const type = interpretValueType(val);
                // Critical error if come across unsupported type so return early
                if (!finley_types_1.typeguards.datalake.isAthenaResultSchemaType(type)) {
                    errors.push(`Came across unsupported Athena Result Schema type [val:${val}] [type:${type}]`);
                    return { isValid: false, errors };
                }
                const seenTypesSet = (_a = keyTypeMap.get(key)) !== null && _a !== void 0 ? _a : new Set();
                seenTypesSet.add(type);
                keyTypeMap.set(key, seenTypesSet);
            }
            catch (error) {
                errors.push(`Came across unsupported Athena Result Schema type [key:${key}] [val:${val}] [type:${typeof val}]`);
                return { isValid: false, errors };
            }
        }
    }
    for (const [key, typeSet] of keyTypeMap.entries()) {
        const typeArray = Array.from(typeSet);
        if (typeArray.length > 1) {
            const seenTypes = Array.from(typeSet).join(', ');
            errors.push(`Came across multiple types for [key:${key}] [types:${seenTypes}]`);
        }
        else {
            const type = typeArray[0];
            schema.push({ key, type });
        }
    }
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    return { isValid: true, schema };
};
exports.constructAthenaResultSchema = constructAthenaResultSchema;
/**
 * "allMaxCount" refers to the max "maxCount" attribute across all referenced
 * global recurring date configs. Recall that this figure is user authored and denotes
 * how many the user can expect as the max output, helping populate variables in case
 * the dynamic output doesn't generate a long enough list
 *
 * "maxAllAvailableCount" refers to the actual results of generating the lists and
 * denotes what every list has at least the number of dates for
 */
const manageAthenaRecurringDateConfigOutputs = (params) => {
    const { globalRecurringDateConfigFields, globalRecurringDateConfigs, supplementalVariables, athenaQueryName, } = params;
    let allMaxCount = 0;
    const dateConfigResults = [];
    const errors = [];
    let maxAllAvailableCount = Infinity;
    for (const dateField of globalRecurringDateConfigFields) {
        const targetDateConfig = globalRecurringDateConfigs.find(({ field }) => field === dateField);
        if (!targetDateConfig) {
            errors.push(`Missing global recurring date config [athenaQueryName:${athenaQueryName}] [field:${dateField}]`);
            continue;
        }
        const dateListResults = finley_date_1.recurringDate.generateDateListFromRecurringDateConfig({ recurringDateConfig: targetDateConfig, supplementalVariables });
        if (!dateListResults.isValid) {
            dateListResults.errors.forEach((err) => errors.push(err));
            continue;
        }
        const { dateList } = dateListResults;
        const { field, maxCount } = targetDateConfig;
        allMaxCount = Math.max(allMaxCount, maxCount);
        maxAllAvailableCount = Math.min(maxAllAvailableCount, dateList.length);
        dateConfigResults.push({ field, dateList });
    }
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    // If never got reduced, then default to 0
    if (maxAllAvailableCount === Infinity) {
        maxAllAvailableCount = 0;
    }
    return {
        isValid: true,
        allMaxCount,
        maxAllAvailableCount,
        dateConfigResults,
    };
};
exports.manageAthenaRecurringDateConfigOutputs = manageAthenaRecurringDateConfigOutputs;
//# sourceMappingURL=construct.js.map